<template>
    <ClientOnlyTeleport>
        <Transition name="pdf-lightbox">
            <div v-if="visible" class="pdf-lightbox" @keydown.escape="close" tabindex="-1" ref="root">
                <!-- Backdrop -->
                <div class="pdf-lightbox__bg" @click="close"></div>

                <!-- Top bar -->
                <div class="pdf-lightbox__topbar">
                    <span class="pdf-lightbox__title">{{ filename }}</span>
                    <span v-if="totalPages && progressive" class="pdf-lightbox__page-info" data-testid="pdf-page-indicator">
                        Pagina {{ currentPage }} di {{ totalPages }}
                    </span>
                    <span v-else-if="totalPages" class="pdf-lightbox__page-info">
                        {{ totalPages }} {{ totalPages === 1 ? 'pagina' : 'pagine' }}
                    </span>
                    <button type="button" class="pdf-lightbox__btn" title="Chiudi" @click="close">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <!-- Pages container -->
                <div class="pdf-lightbox__scroll" ref="scrollContainer" @click.self="close">
                    <div v-if="loading" class="pdf-lightbox__loading">
                        <div class="pdf-lightbox__spinner"></div>
                        <span class=" font-normal">Caricamento PDF…</span>
                    </div>
                    <div v-if="error" class="pdf-lightbox__error">
                        Impossibile caricare il PDF.
                    </div>
                    <!-- Progressive: sized placeholders, only the pages near the viewport hold a canvas bitmap. -->
                    <div v-if="progressive" class="pdf-lightbox__pages"
                        :style="{ transform: `scale(${scale})`, transformOrigin: 'top center' }">
                        <div v-for="p in totalPages" :key="p" :ref="el => setSlotRef(el, p)" :data-page="p"
                            class="pdf-lightbox__slot" :style="slotStyle(p)">
                            <canvas :ref="el => setCanvasRef(el, p)" class="pdf-lightbox__canvas" />
                        </div>
                    </div>
                    <div v-else class="pdf-lightbox__pages"
                        :style="{ transform: `scale(${scale})`, transformOrigin: 'top center' }">
                        <canvas v-for="p in totalPages" :key="p" :ref="el => setCanvasRef(el, p)"
                            class="pdf-lightbox__canvas" />
                    </div>
                </div>

                <!-- Bottom toolbar -->
                <div class="pdf-lightbox__toolbar">
                    <button type="button" class="pdf-lightbox__btn" title="Riduci" @click="zoomOut"
                        :disabled="scale <= MIN_SCALE">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                    </button>
                    <span class="pdf-lightbox__zoom-label">{{ Math.round(scale * 100) }}%</span>
                    <button type="button" class="pdf-lightbox__btn" title="Ingrandisci" @click="zoomIn"
                        :disabled="scale >= MAX_SCALE">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            <line x1="11" y1="8" x2="11" y2="14" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                    </button>
                    <div v-if="allowDownload" class="pdf-lightbox__separator"></div>
                    <button v-if="allowDownload" type="button" class="pdf-lightbox__btn" title="Scarica" @click="download">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </button>
                </div>
            </div>
        </Transition>
    </ClientOnlyTeleport>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue';
import ClientOnlyTeleport from '@/Components/ClientOnlyTeleport.vue';
import { downloadAsset } from '@/utils/assetUrl';

const PDF_CACHE_NAME = 'pdf-lightbox-v1';
const pdfBinaryCache = new Map();

const props = defineProps({
    url: { type: String, default: null },
    downloadUrl: { type: String, default: null },
    visible: { type: Boolean, default: false },
    // Large documents (scanned volumes): range requests through the proxy, no whole-file cache,
    // and only the pages near the viewport keep a rendered bitmap.
    progressive: { type: Boolean, default: false },
    // Shown in the top bar instead of the file name taken from the URL.
    title: { type: String, default: null },
    allowDownload: { type: Boolean, default: true },
});

const emit = defineEmits(['close']);

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const ZOOM_STEP = 0.25;
const DEVICE_PIXEL_RATIO = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

const root = ref(null);
const scrollContainer = ref(null);
const loading = ref(false);
const error = ref(false);
const totalPages = ref(0);
const scale = ref(1);
const canvasRefs = {};
let pdfDoc = null;
let renderedPages = new Set();
let renderingPages = new Set();
let activeLoadToken = 0;

const filename = ref('');

// Progressive mode state.
const RANGE_CHUNK_SIZE = 1 << 20;
const PROGRESSIVE_KEEP_PAGES = 6;
const currentPage = ref(1);
const slotSizes = ref({});
const defaultSlotSize = ref(null);
const slotRefs = {};
const nearPages = new Set();
const pagesInView = new Set();
let nearObserver = null;
let viewObserver = null;
let renderLoopActive = false;
let activeLoadingTask = null;

const fileProxyUrl = (url) => `/api/file-proxy?url=${encodeURIComponent(url)}`;
const displayWidth = () => Math.min(900, window.innerWidth - 80);

function normalizePdfCacheKey(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        return `${parsed.origin}${parsed.pathname}`;
    } catch {
        return url.split('?')[0] || url;
    }
}

function clonePdfBuffer(buffer) {
    return buffer.slice(0);
}

async function getCachedPdfBuffer(cacheKey) {
    const memoryBuffer = pdfBinaryCache.get(cacheKey);
    if (memoryBuffer) {
        return clonePdfBuffer(memoryBuffer);
    }

    if (typeof window === 'undefined' || !('caches' in window)) {
        return null;
    }

    const cache = await caches.open(PDF_CACHE_NAME);
    const cachedResponse = await cache.match(cacheKey);
    if (!cachedResponse) {
        return null;
    }

    const buffer = await cachedResponse.arrayBuffer();
    pdfBinaryCache.set(cacheKey, buffer);

    return clonePdfBuffer(buffer);
}

async function storePdfBuffer(cacheKey, buffer) {
    const cachedBuffer = clonePdfBuffer(buffer);
    pdfBinaryCache.set(cacheKey, cachedBuffer);

    if (typeof window === 'undefined' || !('caches' in window)) {
        return;
    }

    const cache = await caches.open(PDF_CACHE_NAME);
    await cache.put(cacheKey, new Response(clonePdfBuffer(buffer), {
        headers: {
            'Content-Type': 'application/pdf',
        },
    }));
}

async function fetchPdfBuffer(url) {
    const cacheKey = normalizePdfCacheKey(url);
    const cachedBuffer = await getCachedPdfBuffer(cacheKey);
    if (cachedBuffer) {
        return cachedBuffer;
    }

    const response = await fetch(fileProxyUrl(url), {
        credentials: 'same-origin',
    });

    if (!response.ok) {
        throw new Error(`PDF fetch failed with status ${response.status}`);
    }

    const buffer = await response.arrayBuffer();
    await storePdfBuffer(cacheKey, buffer);

    return clonePdfBuffer(buffer);
}

function setCanvasRef(el, pageNum) {
    if (el) canvasRefs[pageNum] = el;
    else delete canvasRefs[pageNum];
}

function setSlotRef(el, pageNum) {
    if (el) slotRefs[pageNum] = el;
    else delete slotRefs[pageNum];
}

function slotStyle(pageNum) {
    const size = slotSizes.value[pageNum] ?? defaultSlotSize.value;
    return size ? { width: `${size.width}px`, height: `${size.height}px` } : {};
}

function slotSizeFor(page) {
    const viewport = page.getViewport({ scale: 1 });
    const width = displayWidth();
    return { width, height: width * viewport.height / viewport.width };
}

function close() {
    emit('close');
}

function zoomIn() {
    if (scale.value < MAX_SCALE) scale.value = Math.min(MAX_SCALE, +(scale.value + ZOOM_STEP).toFixed(2));
}

function zoomOut() {
    if (scale.value > MIN_SCALE) scale.value = Math.max(MIN_SCALE, +(scale.value - ZOOM_STEP).toFixed(2));
}

function download() {
    const url = props.downloadUrl || props.url;
    if (url) downloadAsset(url);
}

async function renderPage(pageNum) {
    if (!pdfDoc || renderedPages.has(pageNum) || renderingPages.has(pageNum)) return renderedPages.has(pageNum);

    renderingPages.add(pageNum);
    try {
        const page = await pdfDoc.getPage(pageNum);
        const canvas = canvasRefs[pageNum];
        if (!canvas) return false;

        // Render at a base scale that fills ~900px width, then CSS transform handles zoom
        const baseViewport = page.getViewport({ scale: 1 });
        const desiredWidth = displayWidth();
        const renderScale = (desiredWidth / baseViewport.width) * DEVICE_PIXEL_RATIO;
        const viewport = page.getViewport({ scale: renderScale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / DEVICE_PIXEL_RATIO}px`;
        canvas.style.height = `${viewport.height / DEVICE_PIXEL_RATIO}px`;
        if (props.progressive && !slotSizes.value[pageNum]) {
            slotSizes.value = { ...slotSizes.value, [pageNum]: slotSizeFor(page) };
        }

        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        renderedPages.add(pageNum);
        return true;
    } finally {
        renderingPages.delete(pageNum);
    }
}

/** Frees the bitmaps far from the pages near the viewport: memory stays bounded on long volumes. */
function releaseDistantPages() {
    if (!nearPages.size) return;
    const first = Math.min(...nearPages) - PROGRESSIVE_KEEP_PAGES;
    const last = Math.max(...nearPages) + PROGRESSIVE_KEEP_PAGES;
    for (const pageNum of [...renderedPages]) {
        if (pageNum >= first && pageNum <= last) continue;
        const canvas = canvasRefs[pageNum];
        if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
        }
        renderedPages.delete(pageNum);
    }
}

/** Renders the pages near the viewport one at a time, closest to the current page first. */
async function renderNearPages(loadToken) {
    releaseDistantPages();
    if (renderLoopActive) return;
    renderLoopActive = true;
    try {
        while (loadToken === activeLoadToken && pdfDoc) {
            const next = [...nearPages]
                .filter((pageNum) => !renderedPages.has(pageNum))
                .sort((a, b) => Math.abs(a - currentPage.value) - Math.abs(b - currentPage.value))[0];
            if (next === undefined) break;
            if (!await renderPage(next)) nearPages.delete(next);
            await new Promise((resolve) => setTimeout(resolve, 0));
        }
    } finally {
        renderLoopActive = false;
    }
}

function disconnectObservers() {
    nearObserver?.disconnect();
    viewObserver?.disconnect();
    nearObserver = null;
    viewObserver = null;
    nearPages.clear();
    pagesInView.clear();
}

function observePages(loadToken) {
    disconnectObservers();
    if (typeof IntersectionObserver === 'undefined') {
        renderRemainingPages(loadToken).catch((e) => console.error('PDF background render error:', e));
        return;
    }
    const root = scrollContainer.value;
    const pageOf = (entry) => Number(entry.target.dataset.page);
    // One viewport of margin above and below: pages are ready before they scroll in.
    nearObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) nearPages.add(pageOf(entry));
            else nearPages.delete(pageOf(entry));
        }
        renderNearPages(loadToken).catch((e) => console.error('PDF render error:', e));
    }, { root, rootMargin: '100% 0px' });
    viewObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) pagesInView.add(pageOf(entry));
            else pagesInView.delete(pageOf(entry));
        }
        if (pagesInView.size) currentPage.value = Math.min(...pagesInView);
    }, { root, threshold: 0 });
    for (const el of Object.values(slotRefs)) {
        nearObserver.observe(el);
        viewObserver.observe(el);
    }
}

/** Sizes the placeholders from the page dictionaries, without fetching page contents. */
async function measurePages(loadToken) {
    let measured = {};
    for (let i = 2; i <= (pdfDoc?.numPages ?? 0); i++) {
        if (loadToken !== activeLoadToken || !pdfDoc) return;
        const page = await pdfDoc.getPage(i);
        if (!slotSizes.value[i]) measured[i] = slotSizeFor(page);
        if (i % 25 === 0 || i === pdfDoc.numPages) {
            slotSizes.value = { ...measured, ...slotSizes.value };
            measured = {};
        }
    }
}

async function loadProgressivePdf(pdfjsLib, url, loadToken) {
    const loadingTask = pdfjsLib.getDocument({
        url: fileProxyUrl(url),
        withCredentials: true,
        rangeChunkSize: RANGE_CHUNK_SIZE,
        disableAutoFetch: true,
        disableStream: true,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.5.207/cmaps/',
        cMapPacked: true,
    });
    activeLoadingTask = loadingTask;
    const doc = await loadingTask.promise;
    if (loadToken !== activeLoadToken) return;
    pdfDoc = doc;
    defaultSlotSize.value = slotSizeFor(await pdfDoc.getPage(1));
    totalPages.value = pdfDoc.numPages;
    currentPage.value = 1;

    await nextTick();
    if (loadToken !== activeLoadToken || !pdfDoc) return;

    await renderPage(1);
    loading.value = false;
    observePages(loadToken);
    measurePages(loadToken).catch((e) => console.error('PDF page sizes error:', e));
}

function resetProgressive() {
    disconnectObservers();
    activeLoadingTask?.destroy?.();
    activeLoadingTask = null;
    slotSizes.value = {};
    defaultSlotSize.value = null;
    currentPage.value = 1;
}

async function renderRemainingPages(loadToken, startPage = 2) {
    if (!pdfDoc) return;

    for (let i = startPage; i <= pdfDoc.numPages; i++) {
        if (loadToken !== activeLoadToken || !pdfDoc) {
            return;
        }

        await renderPage(i);
        await new Promise((resolve) => setTimeout(resolve, 0));
    }
}

async function loadPdf(url) {
    if (!url) return;

    const loadToken = ++activeLoadToken;

    loading.value = true;
    error.value = false;
    totalPages.value = 0;
    scale.value = 1;
    renderedPages = new Set();
    pdfDoc = null;
    if (props.progressive) resetProgressive();

    // Extract filename from URL
    const path = url.split('?')[0];
    filename.value = props.title || decodeURIComponent(path.split('/').pop() || 'PDF');

    try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.mjs',
            import.meta.url
        ).href;
        if (props.progressive) {
            await loadProgressivePdf(pdfjsLib, url, loadToken);
            return;
        }
        const pdfBuffer = await fetchPdfBuffer(url);

        const loadingTask = pdfjsLib.getDocument({
            data: pdfBuffer,
            cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.5.207/cmaps/',
            cMapPacked: true,
        });
        pdfDoc = await loadingTask.promise;
        totalPages.value = pdfDoc.numPages;

        await nextTick();

        if (loadToken !== activeLoadToken || !pdfDoc) {
            return;
        }

        await renderPage(1);
        loading.value = false;

        renderRemainingPages(loadToken).catch((e) => {
            console.error('PDF background render error:', e);
        });
    } catch (e) {
        console.error('PDF loading error:', e);
        error.value = true;
    } finally {
        if (loadToken === activeLoadToken && renderedPages.size === 0) {
            loading.value = false;
        }
    }
}

watch(() => props.visible, async (isVisible) => {
    if (isVisible && props.url) {
        await nextTick();
        root.value?.focus();
        loadPdf(props.url);
        document.body.style.overflow = 'hidden';
    } else {
        activeLoadToken++;
        document.body.style.overflow = '';
        if (props.progressive) resetProgressive();
        pdfDoc = null;
        renderedPages = new Set();
        totalPages.value = 0;
        loading.value = false;
    }
});

onBeforeUnmount(() => {
    activeLoadToken++;
    document.body.style.overflow = '';
    if (props.progressive) resetProgressive();
    pdfDoc = null;
});
</script>

<style scoped>
.pdf-lightbox {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    outline: none;
}

.pdf-lightbox__bg {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

/* ── Top bar ────────────────────────────────────────────────────────── */
.pdf-lightbox__topbar {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    background: rgba(0, 0, 0, 0.5);
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    user-select: none;
}

.pdf-lightbox__title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
}

.pdf-lightbox__page-info {
    color: rgba(255, 255, 255, 0.55);
    font-size: 13px;
    white-space: nowrap;
}

/* ── Scrollable area ────────────────────────────────────────────────── */
.pdf-lightbox__scroll {
    position: relative;
    z-index: 1;
    flex: 1;
    overflow: auto;
    display: flex;
    justify-content: center;
}

.pdf-lightbox__pages {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px 40px 80px;
    transition: transform 0.15s ease;
}

.pdf-lightbox__canvas {
    display: block;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.4);
    background: white;
    border-radius: 2px;
}

/* Progressive mode: the placeholder keeps the page size while its bitmap is not rendered. */
.pdf-lightbox__slot {
    flex: none;
    background: white;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.4);
    border-radius: 2px;
    overflow: hidden;
}

.pdf-lightbox__slot .pdf-lightbox__canvas {
    box-shadow: none;
}

/* ── Loading / Error ────────────────────────────────────────────────── */
.pdf-lightbox__loading,
.pdf-lightbox__error {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: rgba(255, 255, 255, 0.8);
    font-size: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    z-index: 2;
}

.pdf-lightbox__spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: pdf-spin 0.7s linear infinite;
}

@keyframes pdf-spin {
    to {
        transform: rotate(360deg);
    }
}

/* ── Bottom toolbar (PhotoSwipe style) ──────────────────────────────── */
.pdf-lightbox__toolbar {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border-radius: 10px;
    padding: 6px;
}

.pdf-lightbox__separator {
    width: 1px;
    height: 24px;
    background: rgba(255, 255, 255, 0.2);
    margin: 0 2px;
}

.pdf-lightbox__zoom-label {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    min-width: 42px;
    text-align: center;
    user-select: none;
}

/* ── Buttons (same style as .pswp__toolbar-btn) ─────────────────────── */
.pdf-lightbox__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.pdf-lightbox__btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
}

.pdf-lightbox__btn:active {
    background: rgba(255, 255, 255, 0.25);
}

.pdf-lightbox__btn:disabled {
    opacity: 0.3;
    cursor: default;
}

.pdf-lightbox__btn svg {
    width: 100%;
    height: 100%;
}

/* ── Transition ─────────────────────────────────────────────────────── */
.pdf-lightbox-enter-active,
.pdf-lightbox-leave-active {
    transition: opacity 0.25s ease;
}

.pdf-lightbox-enter-from,
.pdf-lightbox-leave-to {
    opacity: 0;
}
</style>
